import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Alert,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import BuildIcon from "@mui/icons-material/Build";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { UploadDropzone } from "./UploadDropzone";
import { preventiviApi } from "../../services/preventiviApi";
import toast from "react-hot-toast";

interface ImportFileDialogProps {
  open: boolean;
  preventivoId: string;
  defaultDisciplina?: "ELE" | "MEC";
  onClose: () => void;
  onImported: () => void;
}

export const ImportFileDialog: React.FC<ImportFileDialogProps> = ({
  open,
  preventivoId,
  defaultDisciplina = "ELE",
  onClose,
  onImported,
}) => {
  const [disciplina, setDisciplina] = useState<"ELE" | "MEC">(defaultDisciplina);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setDisciplina(defaultDisciplina);
  }, [open, defaultDisciplina]);

  const reset = () => {
    setDisciplina(defaultDisciplina);
    setFile(null);
    setLoading(false);
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const result = await preventiviApi.importFile(preventivoId, file, disciplina);
      toast.success(`${result.added} righe importate (${disciplina}) — elaborazione avviata`);
      reset();
      onImported();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Errore durante l'importazione");
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "1rem" } }}
    >
      <DialogTitle sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
        <UploadFileIcon sx={{ color: "text.secondary" }} />
        Importa file in questo preventivo
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 0.5 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Seleziona la disciplina del file da importare. Le righe verranno aggiunte a quelle
              esistenti e avvierà automaticamente la ricerca sui nuovi elementi.
            </Typography>
            <ToggleButtonGroup
              value={disciplina}
              exclusive
              onChange={(_, v) => { if (v) setDisciplina(v); }}
              disabled={loading}
              size="small"
              sx={{ width: "100%" }}
            >
              <ToggleButton
                value="ELE"
                sx={{
                  flex: 1,
                  gap: 1,
                  "&.Mui-selected": { bgcolor: "#E3F2FD", color: "#1565C0", borderColor: "#1565C0" },
                }}
              >
                <BoltIcon sx={{ fontSize: "1.1rem" }} />
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                  Elettrico (ELE)
                </Typography>
              </ToggleButton>
              <ToggleButton
                value="MEC"
                sx={{
                  flex: 1,
                  gap: 1,
                  "&.Mui-selected": { bgcolor: "#FFF3E0", color: "#E65100", borderColor: "#E65100" },
                }}
              >
                <BuildIcon sx={{ fontSize: "1.1rem" }} />
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                  Meccanico (MEC)
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <UploadDropzone
            file={file}
            onChange={(f) => { setFile(f); setError(null); }}
            disabled={loading}
            placeholder={`Trascina il file BOM ${disciplina === "ELE" ? "elettrico" : "meccanico"} (.xlsx, .xls)`}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>Annulla</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading || !file}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? "Importazione..." : "Importa file"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
